/**
 * Arquivos e anexos — helpers puros do visualizador e das telas que listam
 * documento (anexo do lead no cockpit, aba Documentos do Cliente 360).
 *
 * NÃO confundir com `utils/documento.js` (singular), que é CPF/CNPJ.
 *
 * Por que existe (CRM-50): `detectarKind`/`estrategia` viviam presos dentro do
 * DocumentViewerModal e `formatarTamanho` tinha três cópias (AnexosBlock,
 * AnexosBloco, DocumentosTab). Como o anexo do lead passou a usar o mesmo
 * visualizador do 360, as duas telas precisam da MESMA régua — senão o mesmo
 * arquivo ganha ícone e tamanho diferentes em cada uma.
 *
 * O download é sempre pelo proxy autenticado (`download_url`), nunca pela URL
 * assinada do S3: a assinada expira em 1 h com a aba aberta e, quando o
 * arquivo sumiu do bucket, entrega a página XML NoSuchKey da AWS no lugar do
 * 404 legível do backend.
 */
import { ACCESS_API_URL as API_BASE } from "../config/api";
import { mensagemDeErro } from "./errosApi";

const CHAVE_SESSAO = "pharuerp:access-session";

const EXTENSOES_IMAGEM = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "avif"];
const EXTENSOES_TEXTO = ["txt", "log", "json", "xml", "md", "yml", "yaml", "ini", "csv"];

/** Extensões e teto que o backend aceita em LeadAnexo (crm/models.py). */
export const EXTENSOES_ANEXO_LEAD = [
  "pdf", "png", "jpg", "jpeg", "heic", "doc", "docx", "xls", "xlsx", "dwg", "skp",
];
export const MAX_BYTES_ANEXO_LEAD = 10 * 1024 * 1024;

/** String pronta para o `accept` do input file. */
export const ACCEPT_ANEXO_LEAD = EXTENSOES_ANEXO_LEAD.map((e) => `.${e}`).join(",");

// ---------- identificação do arquivo ----------

/** Extensão em minúsculas, a partir do nome (ou da URL, se não houver nome). */
export function extensaoDoArquivo(doc) {
  const base = String(doc?.nome || doc?.url || "").split("?")[0].split("#")[0];
  const partes = base.split("/").pop().split(".");
  return partes.length > 1 ? partes.pop().toLowerCase() : "";
}

/** Como o visualizador deve renderizar: pdf/image/xlsx/csv/docx/html/text/unsupported. */
export function detectarKind(doc) {
  const m = String(doc?.mime || "").toLowerCase();
  const e = extensaoDoArquivo(doc);
  if (m.includes("pdf") || e === "pdf") return "pdf";
  if (m.startsWith("image/") || EXTENSOES_IMAGEM.includes(e)) return "image";
  if (e === "xlsx" || e === "xls" || m.includes("spreadsheetml") || m.includes("ms-excel"))
    return "xlsx";
  if (e === "csv" || m.includes("csv")) return "csv";
  if (e === "docx" || m.includes("wordprocessingml")) return "docx";
  if (m.includes("html") || e === "html" || e === "htm") return "html";
  if (m.startsWith("text/") || EXTENSOES_TEXTO.includes(e)) return "text";
  return "unsupported";
}

/** Caminho relativo -> URL absoluta da API. Absoluta passa intacta. */
export function urlAbsoluta(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE}${url.startsWith("/") ? "" : "/"}${url}`;
}

/**
 * Como carregar o documento:
 * - "direto": a URL vai no src do iframe/img sem fetch. Vale para a rota do
 *   frontend (contrato `/p/contrato/...`) e para URLs absolutas externas
 *   (gateway fiscal, bucket assinado) — um fetch com Authorization numa URL
 *   assinada do S3 falha.
 * - "blob": fetch com JWT + objectURL. Vale para caminho relativo do backend
 *   (o `download_url` do anexo) e para URL absoluta do próprio backend. Só
 *   neste caminho os parsers de xlsx/csv/docx rodam.
 * - "nenhum": sem arquivo.
 */
export function estrategia(doc, kind) {
  const url = doc?.url || "";
  if (!url) return "nenhum";
  if (kind === "html") return "direto"; // rota do frontend (contrato)
  const ehAbsoluto = /^https?:\/\//i.test(url);
  if (ehAbsoluto && !url.startsWith(API_BASE)) return "direto"; // externo
  return "blob";
}

// ---------- apresentação ----------

/** Tamanho legível. `null`/0 devolve "—" (o backend pode não saber o tamanho). */
export function formatarTamanho(bytes) {
  const n = Number(bytes);
  if (!n || Number.isNaN(n)) return "—";
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

/**
 * Categoria visual do anexo. Espelha `comercial.services._tipo_anexo` (backend)
 * de propósito: o MESMO arquivo tem de cair na mesma categoria no cockpit do
 * lead e na aba Documentos do cliente, que é quem já classificava.
 */
export function classificarTipoDocumento({ mime, descricao, nome } = {}) {
  const texto = `${descricao || ""} ${nome || ""}`.toLowerCase();
  if (String(mime || "").startsWith("image")) return "FOTO";
  if (texto.includes("projeto") || texto.includes("planta") || texto.includes("3d"))
    return "PROJETO";
  if (texto.includes("contrato")) return "CONTRATO";
  if (texto.includes("orcament") || texto.includes("orçament")) return "ORCAMENTO";
  return "OUTROS";
}

/**
 * Anexo da API (LeadAnexo/OrcamentoAnexo) -> documento do DocumentoGrid.
 *
 * As chaves do anexo (`nome_original`, `mime_type`, `tamanho_bytes`,
 * `created_at`, `descricao`) não são as que o grid e o visualizador leem
 * (`nome`, `mime`, `tamanho`, `data`, `situacao`) — este adaptador é o único
 * lugar que sabe disso. `id` fica o UUID puro porque a exclusão o exige.
 */
export function anexoParaDocumento(anexo, { fonte = "Anexo de lead" } = {}) {
  const nome = anexo?.nome_original || "arquivo";
  return {
    id: anexo?.id,
    nome,
    // Proxy autenticado do backend; `arquivo_url` (assinada) não existe mais.
    url: anexo?.download_url || "",
    mime: anexo?.mime_type || "",
    tamanho: anexo?.tamanho_bytes ?? null,
    data: anexo?.created_at || null,
    situacao: anexo?.descricao || "",
    situacao_tom: "neutral",
    fonte,
    autor: anexo?.criado_por_nome || "",
    tipo: classificarTipoDocumento({
      mime: anexo?.mime_type,
      descricao: anexo?.descricao,
      nome,
    }),
  };
}

// ---------- validação do upload ----------

/**
 * Mesma régua do backend (`crm/services.py`), aplicada antes de subir 10 MB
 * para receber um 400. Devolve a mensagem de erro ou `null` se está válido.
 */
export function validarArquivoAnexo(
  arquivo,
  { maxBytes = MAX_BYTES_ANEXO_LEAD, extensoes = EXTENSOES_ANEXO_LEAD } = {},
) {
  if (!arquivo) return "Selecione um arquivo.";
  if (arquivo.size > maxBytes) {
    return `Arquivo maior que ${formatarTamanho(maxBytes)}.`;
  }
  const ext = extensaoDoArquivo({ nome: arquivo.name });
  if (!ext) return "Arquivo sem extensão.";
  if (!extensoes.includes(ext)) {
    return `Extensão .${ext} não aceita. Aceitas: ${extensoes.join(", ")}.`;
  }
  return null;
}

// ---------- download autenticado ----------

export function tokenDaSessao() {
  try {
    const bruto = window.localStorage.getItem(CHAVE_SESSAO);
    return (bruto ? JSON.parse(bruto)?.accessToken : null) || null;
  } catch {
    // localStorage indisponível (modo privado): segue sem Bearer e o backend
    // responde 401, que o chamador já sabe exibir.
    return null;
  }
}

const CHAVES_DE_ERRO = ["erro", "detalhe", "detail", "erros", "mensagem"];

async function erroDaResposta(resposta) {
  // O proxy responde 404 {"detalhe": "Arquivo indisponível — o anexo não está
  // mais armazenado."}. Sem ler o corpo, o usuário via só "HTTP 404" — que é
  // o que a tela mostrava no lugar do XML NoSuchKey da AWS.
  let payload = null;
  try {
    const texto = await resposta.text();
    payload = texto ? JSON.parse(texto) : null;
  } catch {
    payload = null; // corpo binário ou HTML: cai na mensagem genérica
  }
  const temMensagem =
    payload && typeof payload === "object"
    && CHAVES_DE_ERRO.some((chave) => payload[chave]);
  const erro = new Error(
    temMensagem
      ? mensagemDeErro(payload, resposta.status)
      : `Não foi possível carregar o arquivo (HTTP ${resposta.status}).`,
  );
  erro.status = resposta.status;
  return erro;
}

function baixar(url, token, signal) {
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(urlAbsoluta(url), { headers, signal });
}

/**
 * Busca o arquivo como blob com o JWT da sessão.
 *
 * `renovarSessao` (opcional) é chamado UMA vez em 401 — o access dura 15 min e
 * um cockpit aberto a manhã inteira estourava esse prazo, mostrando "HTTP 401"
 * no visualizador. Quem passa a função é o consumidor, que tem o `request` do
 * AuthContext (dono do mutex de refresh); aqui só relemos o token depois.
 */
export async function buscarArquivoComAuth(url, { signal, renovarSessao } = {}) {
  let resposta = await baixar(url, tokenDaSessao(), signal);

  if (resposta.status === 401 && renovarSessao) {
    const renovou = await Promise.resolve()
      .then(renovarSessao)
      .then(() => true, () => false);
    // Sem renovação não adianta repetir com o mesmo token: cai no erro abaixo.
    if (renovou) resposta = await baixar(url, tokenDaSessao(), signal);
  }

  if (!resposta.ok) throw await erroDaResposta(resposta);
  return resposta.blob();
}
